import { useState, useCallback, useEffect } from 'react';
import { apiFetch } from '@/lib/api';
import { toast } from '@/hooks/useToast';

export type CopySightSource = 'runner' | 'onDemand';

const SOURCE_LABELS: Record<CopySightSource, string> = {
  runner: 'Runner outputs',
  onDemand: 'On-demand outputs',
};

export interface CopySightSettings {
  autoInspect: Record<CopySightSource, boolean>;
  updatedAt?: string;
  updatedBy?: string;
}

interface UseCopySightSettingsReturn {
  settings: CopySightSettings | null;
  loading: boolean;
  // The source currently being saved, so only that switch is disabled
  savingSource: CopySightSource | null;
  error: string | null;
  setAutoInspect: (source: CopySightSource, enabled: boolean) => Promise<void>;
}

export const useCopySightSettings = (): UseCopySightSettingsReturn => {
  const [settings, setSettings] = useState<CopySightSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [savingSource, setSavingSource] = useState<CopySightSource | null>(null);
  const [error, setError] = useState<string | null>(null);

  const fetchSettings = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const response = await apiFetch('/settings/copysight');
      if (!response.ok) {
        throw new Error(`Failed to fetch CopySight settings: ${response.status}`);
      }
      const data = await response.json();
      setSettings(data.settings);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Unknown error');
    } finally {
      setLoading(false);
    }
  }, []);

  const setAutoInspect = useCallback(async (source: CopySightSource, enabled: boolean): Promise<void> => {
    setSavingSource(source);
    setError(null);
    try {
      const response = await apiFetch('/settings/copysight', {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ autoInspect: { [source]: enabled } }),
      });
      if (!response.ok) {
        throw new Error(`Failed to save CopySight settings: ${response.status}`);
      }
      const data = await response.json();
      setSettings(data.settings);
      toast({
        title: `${SOURCE_LABELS[source]} auto-inspection ${enabled ? 'enabled' : 'disabled'}`,
        description: 'Applies within about 30 seconds.',
      });
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Unknown error';
      setError(message);
      toast({
        title: `Could not update ${SOURCE_LABELS[source].toLowerCase()} auto-inspection`,
        description: message,
        variant: 'destructive',
      });
    } finally {
      setSavingSource(null);
    }
  }, []);

  useEffect(() => {
    fetchSettings();
  }, [fetchSettings]);

  return { settings, loading, savingSource, error, setAutoInspect };
};
